/**
 * Shipments API Client
 * Clean API layer - HTTP calls, validation, transformation only
 */

import { getHttpClient, isNumericId, normalizeEntity } from '@spcc/core'

import type { Shipment, ShipmentStats, ShipmentsFilter, PaginatedShipments, PaginationMeta, OrderLifecycleResponse } from '../../domain/types'
import type { OrderScanResponse, ResourceAssignment, ResourceInput, ResourceSuggestion, StartProcessingResponse } from '../../api/scan-types'
import type { WeighbridgeTicket, FirstWeighRequest, SecondWeighRequest, MarkDeliveredRequest } from '../../api/weighbridge-types'

import {
  MAX_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
} from '../../domain/constants'
import { SHIPMENT_ENDPOINTS } from '../../api/api-types'

import {
  transformShipmentResponseToDomain,
  transformShipmentsResponseToDomain,
  transformFilterToParams,
} from '../../api/transformers'
import { validateShipmentStats } from '../../api'

// ============================================
// Query Builders
// ============================================

function buildPopulateParams(): URLSearchParams {
  const params = new URLSearchParams()
  params.set('populate[order][populate][variant][populate][sku][populate][categories]', 'true')
  params.set('populate[order][populate][dropoff]', 'true')
  params.set('populate[origin]', 'true')
  params.set('populate[destination]', 'true')
  params.set('populate[resource][populate][driver]', 'true')
  params.set('populate[resource][populate][powerUnit]', 'true')
  params.set('populate[resource][populate][trailer]', 'true')
  params.set('populate[tenant]', 'true')
  params.set('populate[factory]', 'true')
    return params
}

function buildListParams(filter?: ShipmentsFilter): URLSearchParams {
  const params = buildPopulateParams()
  params.set('pagination[pageSize]', String(MAX_PAGE_SIZE))

  const filterParams = transformFilterToParams(filter)
  for (const [key, value] of filterParams.entries()) {
    params.set(key, value)
  }

  return params
}

function buildPaginatedListParams(filter?: ShipmentsFilter): URLSearchParams {
  const params = buildPopulateParams()

  // Pagination
  const page = filter?.page ?? 1
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
  params.set('pagination[page]', String(page))
  params.set('pagination[pageSize]', String(pageSize))

  const filterParams = transformFilterToParams(filter)
  for (const [key, value] of filterParams.entries()) {
    params.set(key, value)
  }

  return params
}

// ============================================
// API Client
// ============================================

export const shipmentsApi = {
  async getShipments(filter?: ShipmentsFilter): Promise<Shipment[]> {
    const http = getHttpClient()
    const params = buildListParams(filter)

    const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.LIST}?${params}`)
    const raw = Array.isArray((response.data as Record<string, unknown>)?.data)
      ? (response.data as Record<string, unknown>).data as unknown[]
      : []

    let shipments = transformShipmentsResponseToDomain(raw)

    // Client-side status filter (for cases where API filter doesn't match exactly)
    if (filter?.status && filter.status !== 'all') {
      shipments = shipments.filter(s => s.status === filter.status)
    }

    // Client-side search filter
    if (filter?.search) {
      const search = filter.search.toLowerCase()
      shipments = shipments.filter(
        s => s.shipmentNumber.toLowerCase().includes(search) ||
             s.orderNumber.toLowerCase().includes(search)
      )
    }

    return shipments
  },

  async getShipmentsPaginated(filter?: ShipmentsFilter): Promise<PaginatedShipments> {
    const http = getHttpClient()
    const params = buildPaginatedListParams(filter)

    const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.LIST}?${params}`)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data)
      ? responseData.data as unknown[]
      : []

    let shipments = transformShipmentsResponseToDomain(raw)

    // Client-side status filter (for cases where API filter doesn't match exactly)
    if (filter?.status && filter.status !== 'all') {
      shipments = shipments.filter(s => s.status === filter.status)
    }

    // Client-side search filter
    if (filter?.search) {
      const search = filter.search.toLowerCase()
      shipments = shipments.filter(
        s => s.shipmentNumber.toLowerCase().includes(search) ||
             s.orderNumber.toLowerCase().includes(search)
      )
    }

    // Extract pagination from response
    const meta = responseData?.meta as Record<string, unknown> | undefined
    const paginationData = meta?.pagination as Record<string, unknown> | undefined

    const pagination: PaginationMeta = {
      page: (paginationData?.page as number) ?? filter?.page ?? 1,
      pageSize: (paginationData?.pageSize as number) ?? filter?.pageSize ?? DEFAULT_PAGE_SIZE,
      pageCount: (paginationData?.pageCount as number) ?? 1,
      total: (paginationData?.total as number) ?? shipments.length,
    }

    return { data: shipments, pagination }
  },

  async getShipment(id: string): Promise<Shipment | null> {
    const http = getHttpClient()
    const params = buildPopulateParams()

    try {
      const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.DETAIL(id)}?${params}`)
      const entity = normalizeEntity((response.data as Record<string, unknown>)?.data)
      if (!entity) return null
      return transformShipmentResponseToDomain(entity)
    } catch (error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) {
        // Fallback: try via list filter
        const fallbackParams = buildPopulateParams()
        if (isNumericId(id)) {
          fallbackParams.set('filters[id][$eq]', id)
        } else {
          fallbackParams.set('filters[documentId][$eq]', id)
        }
        fallbackParams.set('pagination[pageSize]', '1')

        const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.LIST}?${fallbackParams}`)
        const raw = Array.isArray((response.data as Record<string, unknown>)?.data)
          ? (response.data as Record<string, unknown>).data as unknown[]
          : []

        if (raw.length === 0) return null
        return transformShipmentResponseToDomain(raw[0])
      }

      throw error
    }
  },

  async getShipmentStats(): Promise<ShipmentStats> {
    const http = getHttpClient()
    const response = await http.get<unknown>(SHIPMENT_ENDPOINTS.STATS)
    const validated = validateShipmentStats(response.data)
    return validated.data
  },

  async getShipmentsByOrderId(orderId: string): Promise<Shipment[]> {
    const http = getHttpClient()
    const params = buildPopulateParams()
    params.set('pagination[pageSize]', String(MAX_PAGE_SIZE))

    if (isNumericId(orderId)) {
      params.set('filters[order][id][$eq]', orderId)
    } else {
      params.set('filters[order][documentId][$eq]', orderId)
    }

    const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.LIST}?${params}`)
    const raw = Array.isArray((response.data as Record<string, unknown>)?.data)
      ? (response.data as Record<string, unknown>).data as unknown[]
      : []

    return transformShipmentsResponseToDomain(raw)
  },

  /**
   * Get weighbridge tickets for a specific shipment (lazy loaded)
   */
  async getShipmentWeighbridgeTickets(shipment: Shipment): Promise<unknown[]> {
    const http = getHttpClient()
    
    // Use numeric ID if available (for relation filtering), otherwise fetch shipment first
    const numericId = shipment.numericId 
      ? String(shipment.numericId)
      : await this.getShipmentNumericId(shipment.id)
    
    if (!numericId) {
      return [] // Shipment not found
    }
    
    // Query weighbridge tickets using numeric shipment ID
    const params = new URLSearchParams()
    params.set('pagination[pageSize]', '100')
    params.set('sort[0]', 'measuredAt:desc')
    params.set('filters[shipment][id][$eq]', numericId)

    const response = await http.get<unknown>(`/api/weighbridge-tickets?${params}`)
    const raw = Array.isArray((response.data as Record<string, unknown>)?.data)
      ? (response.data as Record<string, unknown>).data as unknown[]
      : []

    return raw
  },

  /**
   * Get weighbridge tickets for a specific order (lazy loaded)
   */
  async getOrderWeighbridgeTickets(orderId: string): Promise<unknown[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    params.set('pagination[pageSize]', '100')
    params.set('sort[0]', 'measuredAt:desc')
    
    if (isNumericId(orderId)) {
      params.set('filters[shipment][order][id][$eq]', orderId)
    } else {
      params.set('filters[shipment][order][documentId][$eq]', orderId)
    }

    const response = await http.get<unknown>(`/api/weighbridge-tickets?${params}`)
    const raw = Array.isArray((response.data as Record<string, unknown>)?.data)
      ? (response.data as Record<string, unknown>).data as unknown[]
      : []

    return raw
  },

  /**
   * Helper: Get numeric ID from documentId
   */
  async getShipmentNumericId(shipmentId: string): Promise<string | null> {
    if (isNumericId(shipmentId)) {
      return shipmentId
    }
    
    const http = getHttpClient()
    const params = new URLSearchParams()
    params.set('filters[documentId][$eq]', shipmentId)
    params.set('fields[0]', 'id')
    params.set('pagination[pageSize]', '1')
    
    const response = await http.get<unknown>(`${SHIPMENT_ENDPOINTS.LIST}?${params}`)
    const shipmentData = Array.isArray((response.data as Record<string, unknown>)?.data)
      ? (response.data as Record<string, unknown>).data as unknown[]
      : []
    
    if (shipmentData.length === 0) {
      return null
    }
    
    const shipment = shipmentData[0] as Record<string, unknown>
    return String(shipment.id)
  },

  /**
   * Scan an order by order number
   */
  async scanOrder(orderNumber: string): Promise<OrderScanResponse> {
    const http = getHttpClient()
    const response = await http.get<{ data: OrderScanResponse }>(`/api/fulfillment/scan/${orderNumber}`)
    return response.data.data
  },

  /**
   * Suggest resources (drivers, vehicles)
   */
  async suggestResources(
    orderNumber: string,
    input: { licensePlate?: string | null; driverPhone?: string | null }
  ): Promise<ResourceSuggestion[]> {
    const http = getHttpClient()
    const response = await http.post<{ data: { suggestions: ResourceSuggestion[] } }>(
      '/api/fulfillment/resource-suggest',
      {
        orderNumber,
        licensePlate: input.licensePlate || null,
        driverPhone: input.driverPhone || null,
      }
    )
    return response.data.data.suggestions ?? []
  },

  /**
   * Start processing an order
   */
  async startProcessing(
    input: string | { orderNumber?: string; vehicleLicensePlate?: string }
  ): Promise<StartProcessingResponse> {
    const http = getHttpClient()
    const response = await http.post<{
      data: any
      meta: {
        orderStatusUpdate: { before: string | null; after: string | null; updated: boolean }
      }
    }>(
      '/api/fulfillment/start-processing',
      typeof input === 'string' ? { orderNumber: input } : input
    )

    return {
      order: response.data.data,
      statusUpdate: response.data.meta.orderStatusUpdate,
    }
  },

  /**
   * Assign shipment resource (driver/vehicle) for an order
   */
  async assignResource(orderNumber: string, resource: ResourceInput): Promise<ResourceAssignment> {
    const http = getHttpClient()
    const response = await http.post<{
      data: { assigned: boolean }
      meta: { resourceAssignment: ResourceAssignment }
    }>('/api/fulfillment/assign-resource', {
      orderNumber,
      resource,
    })

    return response.data.meta.resourceAssignment
  },

  /**
   * Record first weigh (tare weight)
   */
  async firstWeigh(request: FirstWeighRequest): Promise<WeighbridgeTicket> {
    const http = getHttpClient()
    const response = await http.post<{ data: WeighbridgeTicket }>('/api/fulfillment/first-weigh', request)
    return response.data.data
  },

  /**
   * Record second weigh (gross weight)
   */
  async secondWeigh(request: SecondWeighRequest): Promise<WeighbridgeTicket> {
    const http = getHttpClient()
    const response = await http.post<{ data: WeighbridgeTicket }>('/api/fulfillment/second-weigh', request)
    return response.data.data
  },

  /**
   * Mark shipment as delivered
   */
  async markDelivered(request: MarkDeliveredRequest): Promise<{ shipment: any; statusUpdate: string }> {
    const http = getHttpClient()
    const response = await http.post<{ data: any; meta: { shipmentStatusUpdate: string } }>('/api/fulfillment/shipment/mark-delivered', request)
    return {
      shipment: response.data.data,
      statusUpdate: response.data.meta.shipmentStatusUpdate,
    }
  },

  /**
   * Join queue at kiosk — triggers SAP Sales Order creation
   */
  async joinQueue(orderId: string, options?: { vehiclePlate?: string; driverName?: string }): Promise<{
    queueEntry: any
    position: number
    sapSalesOrderNumber: string | null
  }> {
    const http = getHttpClient()
    const response = await http.post<any>('/api/queue-entries/join', {
      orderId,
      vehiclePlate: options?.vehiclePlate,
      driverName: options?.driverName,
    })
    const data = response.data?.data ?? response.data
    return {
      queueEntry: data?.queueEntry ?? data,
      position: data?.position ?? 0,
      sapSalesOrderNumber: data?.queueEntry?.order?.sapSalesOrderNumber ?? null,
    }
  },

  /**
   * QR scan reading — triggers full SAP flow (SO creation on entry, exit processing on exit)
   * Sends real payload: { pathId, orderNumber, zoneCode }
   */
  async qrScanReading(payload: {
    pathId: string
    orderNumber: string
    zoneCode: string
  }): Promise<{
    success: boolean
    data: any
    command: any
    meta: any
  }> {
    const http = getHttpClient()
    const response = await http.post<any>('/api/qr-scans/reading', {
      pathId: payload.pathId,
      orderNumber: payload.orderNumber,
      zoneCode: payload.zoneCode,
    })
    return response.data
  },

  /**
   * Camera (ANPR/LPR) reading — triggers gate flow by license plate
   * Sends real payload: { siteId, zoneCode, licensePlate, cameraIp }
   */
  async cameraReading(payload: {
    siteId: string
    zoneCode: string
    licensePlate: string
    cameraIp: string
  }): Promise<{
    success: boolean
    data: any
    command: any
    meta: any
  }> {
    const http = getHttpClient()
    const response = await http.post<any>('/api/cameras/reading', {
      siteId: payload.siteId,
      zoneCode: payload.zoneCode,
      licensePlate: payload.licensePlate,
      cameraIp: payload.cameraIp,
    })
    return response.data
  },

  /**
   * Get full lifecycle state for an order (simulation page)
   */
  async getOrderLifecycle(orderNumber: string): Promise<OrderLifecycleResponse> {
    const http = getHttpClient()
    const response = await http.get<{ data: OrderLifecycleResponse }>(
      `/api/fulfillment/order-lifecycle/${encodeURIComponent(orderNumber)}`
    )
    return response.data.data
  },

  /**
   * Sync invoices from SAP for an order (resolves tenant from order)
   */
  async syncInvoices(orderNumber: string): Promise<any> {
    const http = getHttpClient()
    const response = await http.post<any>(
      `/api/fulfillment/order-lifecycle/${encodeURIComponent(orderNumber)}/sync-invoices`
    )
    return response.data
  },

  async updateShipment(id: string, data: Record<string, any>): Promise<Shipment> {
    const http = getHttpClient()
    const params = buildPopulateParams()
    const response = await http.put<{ data: any }>(`${SHIPMENT_ENDPOINTS.UPDATE(id)}?${params}`, { data })
    const entity = normalizeEntity((response.data as Record<string, unknown>)?.data)
    if (!entity) throw new Error('Failed to update shipment')
    return transformShipmentResponseToDomain(entity)
  },
}
